import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Link, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { recordPath } from '../lib/navHistory';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Progress } from '../components/ui/progress';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '../components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import LegalFooter from '../components/LegalFooter';
import { useEntitlements } from '../contexts/EntitlementsContext';
import { toggleTheme } from '../lib/theme';
import { trackPageView } from '../services/eventTracker';
import NotificationBell from '../components/NotificationBell';
import MissingWebsiteWarning from '../components/MissingWebsiteWarning';
import { OnboardingProvider, OnboardingLayer, HelpMenu } from '../onboarding';
import ChatAgents from '../components/chat/ChatAgents'; // WO-56
import { 
  LayoutDashboard, Newspaper, FileText, Building2, Users, 
  Target, Contact2, BarChart3, Settings, User, CreditCard,
  LogOut, Menu, ChevronRight, Moon, Sun, Globe, Zap,
  TrendingUp, Bell, Search, Sparkles, ArrowUpRight, ArrowDownRight,
  Clock, MapPin, DollarSign, Briefcase, Activity, Eye, Star,
  ChevronDown, Filter, Plus, ExternalLink, Database, UserCog,
  LineChart, PieChart, Server, ScrollText, Cog, Shield, Gift
} from 'lucide-react';

const navItems = [
  { 
    section: 'intelligence',
    items: [
      { key: 'news', icon: Newspaper, href: '/dashboard/news', color: 'from-blue-500 to-cyan-500' },
      { key: 'tenders', icon: FileText, href: '/dashboard/tenders', color: 'from-emerald-500 to-green-500' },
      { key: 'projects', icon: Building2, href: '/dashboard/projects', color: 'from-orange-500 to-amber-500' },
      { key: 'companies', icon: Users, href: '/dashboard/companies', color: 'from-purple-500 to-pink-500' }
    ]
  },
  {
    section: 'crm',
    items: [
      { key: 'opportunities', icon: Target, href: '/dashboard/crm', color: 'from-rose-500 to-red-500' },
      { key: 'contacts', icon: Contact2, href: '/dashboard/contacts', color: 'from-indigo-500 to-blue-500' },
      { key: 'alerts', icon: Bell, href: '/dashboard/alerts', color: 'from-amber-500 to-yellow-500' },
      { key: 'group', icon: UserCog, href: '/dashboard/group', color: 'from-fuchsia-500 to-pink-500' }
    ]
  },
  {
    section: 'analytics',
    items: [
      { key: 'reports', icon: BarChart3, href: '/dashboard/reports', color: 'from-teal-500 to-cyan-500' }
    ]
  },
  {
    section: 'referralsSection',
    items: [
      { key: 'referrals', icon: Gift, href: '/dashboard/referrals', color: 'from-emerald-500 to-teal-500' }
    ]
  },
  {
    section: 'admin',
    items: [
      { key: 'executive', icon: LineChart, href: '/dashboard/admin/executive', color: 'from-violet-500 to-purple-500', adminOnly: true },
      { key: 'revenue', icon: DollarSign, href: '/dashboard/admin/revenue', color: 'from-green-500 to-emerald-500', adminOnly: true },
      { key: 'referralAdmin', icon: Gift, href: '/dashboard/admin/referrals', color: 'from-emerald-500 to-teal-500', adminOnly: true },
      { key: 'accounts', icon: Building2, href: '/dashboard/admin/accounts', color: 'from-blue-500 to-indigo-500', adminOnly: true },
      { key: 'productUsage', icon: Activity, href: '/dashboard/admin/product-usage', color: 'from-amber-500 to-orange-500', adminOnly: true },
      { key: 'marketing', icon: TrendingUp, href: '/dashboard/admin/marketing', color: 'from-pink-500 to-rose-500', adminOnly: true },
      { key: 'funnel', icon: PieChart, href: '/dashboard/admin/funnel', color: 'from-cyan-500 to-teal-500', adminOnly: true },
      { key: 'dataOps', icon: Database, href: '/dashboard/admin/data-operations', color: 'from-slate-500 to-gray-500', adminOnly: true },
      { key: 'tenderSources', icon: Shield, href: '/dashboard/admin/tender-sources', color: 'from-teal-500 to-emerald-500', adminOnly: true },
      { key: 'marketResearch', icon: Sparkles, href: '/dashboard/admin/research', color: 'from-fuchsia-500 to-purple-500', adminOnly: true },
      { key: 'systemHealth', icon: Server, href: '/dashboard/admin/system-health', color: 'from-red-500 to-orange-500', adminOnly: true },
      { key: 'activityLogs', icon: ScrollText, href: '/dashboard/admin/activity-logs', color: 'from-indigo-500 to-violet-500', adminOnly: true },
      { key: 'adminSettings', icon: Cog, href: '/dashboard/admin/settings', color: 'from-gray-500 to-zinc-500', adminOnly: true },
      { key: 'users', icon: UserCog, href: '/dashboard/admin/users', color: 'from-purple-500 to-indigo-500', adminOnly: true },
      { key: 'database', icon: Database, href: '/dashboard/admin/database', color: 'from-slate-500 to-zinc-500', adminOnly: true }
    ]
  }
];

// NOTE: Hardcoded "recentActivity" sample data was removed per DD report (P2-08).
// The dashboard no longer surfaces fictional user activity. A real activity
// feed can be added when backend support is in place.

// Dashboard CSS styles
const dashboardStyles = `
  @keyframes float {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    50% { transform: translateY(-20px) rotate(5deg); }
  }
  
  @keyframes pulse-glow {
    0%, 100% { box-shadow: 0 0 20px rgba(46, 139, 60, 0.3); }
    50% { box-shadow: 0 0 40px rgba(46, 139, 60, 0.6); }
  }
  
  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
  
  @keyframes slide-in {
    0% { opacity: 0; transform: translateX(-20px); }
    100% { opacity: 1; transform: translateX(0); }
  }
  
  @keyframes count-up {
    0% { opacity: 0; transform: translateY(10px); }
    100% { opacity: 1; transform: translateY(0); }
  }
  
  .dashboard-card-3d {
    perspective: 1000px;
    transform-style: preserve-3d;
  }
  
  .dashboard-card-3d:hover {
    transform: translateY(-8px) rotateX(5deg);
    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  }
  
  .glass-card {
    background: rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
  
  .nav-item-glow:hover {
    box-shadow: 0 0 30px rgba(46, 139, 60, 0.4);
  }
  
  .stat-number {
    animation: count-up 0.8s ease-out forwards;
  }
  
  .activity-item {
    animation: slide-in 0.5s ease-out forwards;
  }
  
  .gradient-text {
    background: linear-gradient(135deg, #1F5FA9 0%, #2E8B3C 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  .floating-orb {
    animation: float 8s ease-in-out infinite;
  }
  
  .shimmer-bg {
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent);
    background-size: 200% 100%;
    animation: shimmer 3s ease-in-out infinite;
  }
`;

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { t, isRTL, toggleLanguage, language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [darkMode, setDarkMode] = useState(() => document.documentElement.classList.contains('dark'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeNav, setActiveNav] = useState(location.pathname);

  useEffect(() => {
    setActiveNav(location.pathname);
    
    // Track page view
    const pageName = location.pathname.split('/').pop() || 'dashboard';
    trackPageView(pageName);
  }, [location]);

  // Record path AFTER children render so list pages can detect, on their next
  // mount, whether the user is returning from one of their detail pages.
  useEffect(() => {
    recordPath(location.pathname);
  }, [location.pathname]);

  const toggleDarkMode = () => {
    const next = toggleTheme();
    setDarkMode(next === 'dark');
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Ref to preserve sidebar scroll position
  const sidebarScrollRef = useRef(null);
  const sidebarScrollPos = useRef(0);

  // Restore sidebar scroll position IMMEDIATELY after route changes (before paint)
  useLayoutEffect(() => {
    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      if (sidebarScrollRef.current) {
        sidebarScrollRef.current.scrollTop = sidebarScrollPos.current;
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Handle sidebar navigation click
  const handleNavClick = (href) => {
    // Save current scroll position before navigating
    if (sidebarScrollRef.current) {
      sidebarScrollPos.current = sidebarScrollRef.current.scrollTop;
    }
    setMobileMenuOpen(false);
    navigate(href);
  };

  const SidebarContent = () => (
    <div className={`flex flex-col h-full ${isRTL ? 'font-arabic' : ''}`}>
      {/* Logo */}
      <div className="p-3 border-b border-white/10">
        <Link to="/dashboard" className="block">
          <img src="/logo.png" alt="Mid2Bid" className="w-full h-auto max-h-[50px] object-contain" />
        </Link>
      </div>

      {/* Navigation */}
      <nav ref={sidebarScrollRef} className="flex-1 p-3 space-y-4 overflow-y-auto">
        {navItems.map((group) => {
          // Filter admin-only items for non-admin users
          const filteredItems = group.items.filter(item => 
            !item.adminOnly || (user && user.role === 'admin')
          );
          
          // Skip empty sections
          if (filteredItems.length === 0) return null;
          
          return (
          <div key={group.section}>
            <h3 className={`text-xs font-bold text-muted-foreground/60 uppercase tracking-wider mb-2 px-2 ${isRTL ? 'text-right' : ''}`}>
              {t.dashboard[group.section]}
            </h3>
            <div className="space-y-1">
              {filteredItems.map((item) => {
                const isActive = activeNav === item.href || activeNav.startsWith(item.href + '/');
                return (
                  <button
                    key={item.key}
                    onClick={() => handleNavClick(item.href)}
                    className={`w-full group flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 nav-item-glow
                      ${isActive 
                        ? 'bg-gradient-to-r ' + item.color + ' text-white shadow-lg' 
                        : 'hover:bg-white/5 text-muted-foreground hover:text-foreground'
                      } ${isRTL ? 'flex-row-reverse' : ''}`}
                  >
                    <div className={`p-1.5 rounded-md ${isActive ? 'bg-white/20' : 'bg-gradient-to-br ' + item.color + ' bg-opacity-10'}`}>
                      <item.icon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-white'}`} />
                    </div>
                    <span className="flex-1 text-sm text-left">{t.dashboard[item.key]}</span>
                    {isActive && <ChevronRight className="w-3 h-3" />}
                  </button>
                );
              })}
            </div>
          </div>
        )})}
      </nav>
    </div>
  );

  return (
    <OnboardingProvider>
    <div className={`min-h-screen bg-background ${isRTL ? 'font-arabic' : ''}`}>
      {/* Inject custom styles */}
      <style dangerouslySetInnerHTML={{ __html: dashboardStyles }} />
      
      {/* Animated background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute w-[600px] h-[600px] rounded-full floating-orb"
          style={{
            background: 'radial-gradient(circle, rgba(31, 95, 169, 0.1) 0%, transparent 70%)',
            top: '-200px',
            right: '-200px',
            animationDelay: '0s'
          }}
        />
        <div 
          className="absolute w-[400px] h-[400px] rounded-full floating-orb"
          style={{
            background: 'radial-gradient(circle, rgba(46, 139, 60, 0.1) 0%, transparent 70%)',
            bottom: '-100px',
            left: '-100px',
            animationDelay: '4s'
          }}
        />
      </div>

      {/* Desktop Sidebar */}
      <aside className={`hidden lg:flex flex-col fixed top-0 ${isRTL ? 'right-0' : 'left-0'} w-56 h-screen bg-card/50 backdrop-blur-xl border-r border-white/10 z-40`}>
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side={isRTL ? 'right' : 'left'} className="p-0 w-72 bg-card/95 backdrop-blur-xl border-white/10">
          <SidebarContent />
        </SheetContent>
      </Sheet>

      {/* Main Content */}
      <main className={`${isRTL ? 'lg:mr-56' : 'lg:ml-56'}`}>
        {/* Top Header */}
        <header className="sticky top-0 z-30 bg-background/60 backdrop-blur-xl border-b border-white/10">
          <div className={`flex items-center justify-between h-16 px-4 lg:px-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
            {/* Mobile menu button */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(true)}
              data-onb="menu-button"
            >
              <Menu className="w-5 h-5" />
            </Button>

            {/* Right side actions */}
            <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''} ml-auto`}>
              {/* WO-33 — "?" help menu; renders nothing while onboarding is off. */}
              <HelpMenu />

              {/* Notifications */}
              <NotificationBell />

              {/* Language Toggle */}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={toggleLanguage}
                className="rounded-xl font-medium hover:bg-white/10"
              >
                <Globe className="w-4 h-4 mr-2" />
                {language === 'en' ? 'عربي' : 'EN'}
              </Button>

              {/* Theme Toggle */}
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={toggleDarkMode}
                className="rounded-xl hover:bg-white/10"
              >
                {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className={`gap-3 px-3 py-2 rounded-xl hover:bg-white/10 ${isRTL ? 'flex-row-reverse' : ''}`}
                    data-testid="user-menu-trigger"
                  >
                    <Avatar className="w-9 h-9 ring-2 ring-primary/30">
                      <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white text-sm font-bold">
                        {getInitials(user?.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className={`hidden md:block ${isRTL ? 'text-right' : 'text-left'}`}>
                      <p className="text-sm font-semibold">{user?.name}</p>
                      {/* WO-31 — the role in the reader's language, not the stored code. */}
                      <p className="text-xs text-muted-foreground">
                        {({ admin: { en: 'Admin', ar: 'مسؤول' }, member: { en: 'Member', ar: 'عضو' } }[user?.role] || { en: user?.role, ar: user?.role })[language === 'ar' ? 'ar' : 'en']}
                      </p>
                    </div>
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align={isRTL ? 'start' : 'end'} className="w-56 bg-card/95 backdrop-blur-xl border-white/10">
                  <DropdownMenuLabel className="text-muted-foreground">{user?.email}</DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem onClick={() => navigate('/dashboard/settings?tab=profile')} className="cursor-pointer">
                    <User className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                    {t.dashboard.profile}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/dashboard/settings?tab=plans')} className="cursor-pointer">
                    <Zap className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                    {t.dashboard.plans || 'Plans'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/dashboard/settings?tab=billing')} className="cursor-pointer">
                    <CreditCard className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                    {t.dashboard.billing}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/dashboard/settings?tab=settings')} className="cursor-pointer">
                    <Settings className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                    {t.dashboard.settings}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive cursor-pointer" data-testid="logout-menu-item">
                    <LogOut className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                    {t.dashboard.logout}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Persistent discreet warning when Premium/Enterprise user has no company website */}
        <MissingWebsiteWarning />

        {/* Page Content */}
        <div className="p-4 lg:p-8">
          <Outlet />
        </div>
        {/* WO-26 — on every page inside the app, including the news module. */}
        <LegalFooter language={language} />
      </main>
      {/* WO-33 — welcome window, checklist and tour; nothing while onboarding is off. */}
      <OnboardingLayer />
      {/* WO-56 — the two AI chat assistants; nothing when both are off for this person. */}
      <ChatAgents />
    </div>
    </OnboardingProvider>
  );
}

// Dashboard Home Component
export function DashboardHome() {
  const { user, token } = useAuth();
  const { t, isRTL, language } = useLanguage();
  const { planId, allowedCountries, allCountries } = useEntitlements();
  const navigate = useNavigate();
  const [animatedStats, setAnimatedStats] = useState([0, 0, 0, 0]);
  const [realStats, setRealStats] = useState({ news: 0, tenders: 0, projects: 0, opportunities: 0 });
  const [statsLoaded, setStatsLoaded] = useState(false);

  // Fetch real stats from API
  useEffect(() => {
    const authHeaders = token ? { 'Authorization': `Bearer ${token}` } : {};
    // A tile total is a real number, or null when the request FAILED (so a
    // failure is never printed as a fabricated 0). R2 §1: news/tenders/projects
    // must carry the SAME token opportunities already does, so the server can
    // scope them to the signed-in user's allowed countries.
    const getTotal = (path, withAuth) =>
      fetch(`${process.env.REACT_APP_BACKEND_URL}${path}`, withAuth ? { headers: authHeaders } : {})
        .then(r => (r.ok ? r.json() : { total: null }))
        .then(d => (typeof d.total === 'number' ? d.total : null))
        .catch(() => null);

    const loadStats = async (attempt = 0) => {
      const [news, tenders, projects, opportunities] = await Promise.all([
        getTotal('/api/intelligence/news?page_size=1', true),
        getTotal('/api/intelligence/tenders?page_size=1', true),
        getTotal('/api/intelligence/projects?page_size=1', true),
        token ? getTotal('/api/opportunities/stats', true) : Promise.resolve(null),
      ]);
      const next = { news, tenders, projects, opportunities };
      // R2 §2: one silent retry if a market tile failed; no error dialog.
      if (attempt === 0 && (news === null || tenders === null || projects === null)) {
        setTimeout(() => loadStats(1), 1200);
      }
      setRealStats(next);
      setStatsLoaded(true);
    };

    loadStats();
  }, [token]);

  // Animate stats on mount after loading
  useEffect(() => {
    if (!statsLoaded) return;
    
    // Failed tiles (null) stay at 0 for the animation math; the render shows a
    // dash for them instead of the number.
    const targets = [realStats.news, realStats.tenders, realStats.projects, realStats.opportunities]
      .map(v => (typeof v === 'number' ? v : 0));
    const duration = 2000;
    const steps = 60;
    const stepDuration = duration / steps;
    
    let step = 0;
    const interval = setInterval(() => {
      step++;
      const progress = step / steps;
      const eased = 1 - Math.pow(1 - progress, 3); // Ease out cubic
      setAnimatedStats(targets.map(target => Math.round(target * eased)));
      
      if (step >= steps) clearInterval(interval);
    }, stepDuration);
    
    return () => clearInterval(interval);
  }, [statsLoaded, realStats]);

  // Stats cards with navigation. ``raw`` is the true value (null = request
  // failed) so the render can show a dash instead of a fabricated 0.
  const statsCards = [
    { 
      label: 'Market News', 
      labelAr: 'أخبار السوق',
      raw: realStats.news,
      value: animatedStats[0], 
      icon: Newspaper, 
      color: 'from-blue-500 to-cyan-500',
      href: '/dashboard/news'
    },
    { 
      label: 'Tenders', 
      labelAr: 'المناقصات',
      raw: realStats.tenders,
      value: animatedStats[1], 
      icon: FileText, 
      color: 'from-emerald-500 to-green-500',
      href: '/dashboard/tenders'
    },
    { 
      label: 'Projects Tracked', 
      labelAr: 'المشاريع المتتبَّعة',
      raw: realStats.projects,
      value: animatedStats[2], 
      icon: Building2, 
      color: 'from-orange-500 to-amber-500',
      href: '/dashboard/projects'
    },
    { 
      label: 'My Opportunities', 
      labelAr: 'فرصي',
      raw: realStats.opportunities,
      value: animatedStats[3], 
      icon: Target, 
      color: 'from-rose-500 to-red-500',
      href: '/dashboard/crm'
    }
  ];

  // All navigation cards with AI-generated images - Row 1
  const row1Cards = [
    { 
      key: 'news',
      label: 'News',
      labelAr: 'الأخبار',
      icon: Newspaper, 
      color: 'from-blue-500 to-cyan-500',
      href: '/dashboard/news',
      image: 'https://static.prod-images.emergentagent.com/jobs/88b2451f-6c05-46a3-b184-6eaf4b1a6ffd/images/6f21460534cfc6cc76a9e0519bc9db898a1fab030ca584814622d2a768629724.png'
    },
    { 
      key: 'tenders',
      label: 'Tenders',
      labelAr: 'المناقصات',
      icon: FileText, 
      color: 'from-emerald-500 to-green-500',
      href: '/dashboard/tenders',
      image: 'https://static.prod-images.emergentagent.com/jobs/88b2451f-6c05-46a3-b184-6eaf4b1a6ffd/images/6c92c5f92046d48692f1447f65d7691e8a41397ec958a8fa393975e67a5e7143.png'
    },
    { 
      key: 'projects',
      label: 'Projects',
      labelAr: 'المشاريع',
      icon: Building2, 
      color: 'from-orange-500 to-amber-500',
      href: '/dashboard/projects',
      image: 'https://static.prod-images.emergentagent.com/jobs/88b2451f-6c05-46a3-b184-6eaf4b1a6ffd/images/ea37968681bebdec29d82065da947cc95da51ecf868a18cc416ae45346378758.png'
    },
    { 
      key: 'companies',
      label: 'Companies',
      labelAr: 'الشركات',
      icon: Users, 
      color: 'from-purple-500 to-pink-500',
      href: '/dashboard/companies',
      image: 'https://static.prod-images.emergentagent.com/jobs/88b2451f-6c05-46a3-b184-6eaf4b1a6ffd/images/ec8f15d1701d29b9c892ac6feed3d407a7d0118e5bd20ba9a505db0c77908ca6.png'
    }
  ];

  // All navigation cards with AI-generated images - Row 2
  const row2Cards = [
    { 
      key: 'opportunities',
      label: 'Opportunities',
      labelAr: 'الفرص',
      icon: Target, 
      color: 'from-rose-500 to-red-500',
      href: '/dashboard/crm',
      image: 'https://static.prod-images.emergentagent.com/jobs/88b2451f-6c05-46a3-b184-6eaf4b1a6ffd/images/98f3dbd292e144875aaab79bb5382bbcc011879f3f6dfc308be844e04598ac92.png'
    },
    { 
      key: 'contacts',
      label: 'Contacts',
      labelAr: 'جهات الاتصال',
      icon: Contact2, 
      color: 'from-indigo-500 to-blue-500',
      href: '/dashboard/contacts',
      image: 'https://static.prod-images.emergentagent.com/jobs/88b2451f-6c05-46a3-b184-6eaf4b1a6ffd/images/b73d4a99ec13ac924a24812b6a0590bbb564b53115c90a7b6f86ae44081a5bf9.png'
    },
    { 
      key: 'reports',
      label: 'Reports',
      labelAr: 'التقارير',
      icon: BarChart3, 
      color: 'from-teal-500 to-cyan-500',
      href: '/dashboard/reports',
      image: 'https://static.prod-images.emergentagent.com/jobs/88b2451f-6c05-46a3-b184-6eaf4b1a6ffd/images/9ac52319363f0b71ece23a0686956e45f83e7e6dd095767e71aec72839ab54e4.png'
    },
    { 
      key: 'myprofile',
      label: 'My Profile',
      labelAr: 'ملفي الشخصي',
      icon: User, 
      color: 'from-violet-500 to-purple-500',
      href: '/dashboard/settings',
      image: 'https://static.prod-images.emergentagent.com/jobs/88b2451f-6c05-46a3-b184-6eaf4b1a6ffd/images/d5a0887f53d1601aeea2bd14f7a909afc4b16e6117f4b28ecef14336aff5f1de.png'
    }
  ];

  // Image card component with AI-generated background
  const ImageNavCard = ({ item }) => (
    <Card 
      className="group relative overflow-hidden border-0 hover:shadow-2xl transition-all duration-500 cursor-pointer h-[120px]"
      onClick={() => navigate(item.href)}
      data-testid={`dashboard-nav-${item.key}`}
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
        style={{ backgroundImage: `url(${item.image})` }}
      />
      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20 group-hover:from-black/70 transition-colors" />
      
      {/* Content */}
      <CardContent className="relative h-full p-4 flex flex-col justify-end">
        <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
          <div className={`p-2 rounded-xl bg-gradient-to-br ${item.color} shadow-lg shrink-0`}>
            <item.icon className="w-5 h-5 text-white" />
          </div>
          <h3 className="font-bold text-lg text-white drop-shadow-lg">
            {isRTL ? item.labelAr : item.label}
          </h3>
        </div>
      </CardContent>
    </Card>
  );

  const CN = {
    en: { SA: 'Saudi Arabia', AE: 'the UAE', OM: 'Oman', KW: 'Kuwait', BH: 'Bahrain', QA: 'Qatar' },
    ar: { SA: 'السعودية', AE: 'الإمارات', OM: 'عُمان', KW: 'الكويت', BH: 'البحرين', QA: 'قطر' },
  };
  const planMeta = {
    free: { en: 'Free plan', ar: 'الخطة المجانية', paid: false },
    standard: { en: 'Standard', ar: 'ستاندرد', paid: true },
    premium: { en: 'Premium', ar: 'بريميم', paid: true },
    enterprise: { en: 'Enterprise', ar: 'إنتربرايز', paid: true },
  };
  const pm = planMeta[planId] || planMeta.free;
  const planLabel = language === 'ar' ? pm.ar : pm.en;
  const nCountries = allCountries ? 6 : (allowedCountries?.length || 0);
  const cn = CN[language === 'ar' ? 'ar' : 'en'];
  const heroSentence = (nCountries === 1 && allowedCountries[0])
    ? (language === 'ar'
        ? `لوحة معلومات دول الخليج جاهزة. تابع فرص السوق في ${cn[allowedCountries[0]] || allowedCountries[0]}.`
        : `Your intelligence dashboard is ready. Track market opportunities across ${cn[allowedCountries[0]] || allowedCountries[0]}.`)
    : (language === 'ar'
        ? `لوحة معلومات دول الخليج جاهزة. تابع فرص السوق في جميع دول الخليج الست.`
        : `Your GCC intelligence dashboard is ready. Track market opportunities across all ${nCountries || 6} GCC countries.`);

  return (
    <div className="space-y-4">
      {/* Welcome Hero Section - Compact */}
      <div className="relative overflow-hidden rounded-2xl">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/818775/pexels-photo-818775.jpeg?w=1920)',
            filter: 'brightness(0.3)'
          }}
        />
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary/80 via-primary/50 to-transparent" />
        
        {/* Content */}
        <div className={`relative p-4 lg:p-6 ${isRTL ? 'text-right' : ''}`}>
          <div className="max-w-2xl">
            <Badge className={`mb-2 text-xs ${pm.paid ? 'bg-accent/20 text-accent border-accent/30 hover:bg-accent/30' : 'bg-white/10 text-white/90 border-white/20 hover:bg-white/20'}`} data-testid="dashboard-plan-badge">
              <Sparkles className="w-3 h-3 mr-1" />
              {planLabel}
            </Badge>
            <h1 className="text-2xl lg:text-3xl font-black text-white mb-2" style={{fontFamily: "'Sora', sans-serif"}}>
              {t.dashboard.welcome}, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-sm text-white/80 mb-3" data-testid="dashboard-hero-sentence">
              {heroSentence}
              {!pm.paid && (
                <>
                  {' '}
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard/settings?tab=plans')}
                    className="underline underline-offset-2 text-white hover:text-white/90"
                    data-testid="dashboard-hero-upgrade-link"
                  >
                    {language === 'ar' ? 'الترقية لرؤية كل الأسواق الستة' : 'Upgrade to see all six markets'}
                  </button>
                </>
              )}
            </p>
            <div className={`flex gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Button 
                size="sm" 
                className="bg-white text-primary hover:bg-white/90 font-bold rounded-lg h-8 px-3 text-xs"
                onClick={() => navigate('/dashboard/crm')}
                data-testid="hero-new-opportunity-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                New Opportunity
              </Button>
              <Button 
                size="sm" 
                variant="outline" 
                className="border-white/30 text-white hover:bg-white/10 rounded-lg h-8 px-3 text-xs"
                onClick={() => navigate('/dashboard/reports')}
                data-testid="hero-view-reports-btn"
              >
                <Eye className="w-4 h-4 mr-1" />
                View Reports
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid - Compact */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {statsCards.map((stat, idx) => (
          <div 
            key={idx} 
            className="dashboard-card-3d group cursor-pointer"
            style={{ animationDelay: `${idx * 0.1}s` }}
            onClick={() => navigate(stat.href)}
            data-testid={`stat-card-${idx}`}
          >
            <Card className="relative overflow-hidden border-0 bg-card/50 backdrop-blur-sm hover:shadow-2xl transition-all duration-500">
              {/* Background gradient */}
              <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-5 group-hover:opacity-10 transition-opacity`} />
              
              <CardContent className="p-3 h-[80px]">
                <div className={`flex items-start justify-between h-full ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className="flex flex-col justify-between h-full">
                    <p className="text-xs text-muted-foreground font-medium whitespace-nowrap">{language === 'ar' ? stat.labelAr : stat.label}</p>
                    <p className="text-2xl font-black stat-number" data-testid={`stat-value-${idx}`}>
                      {stat.raw === null ? '—' : String(stat.value)}
                    </p>
                  </div>
                  <div className={`p-2 rounded-xl bg-gradient-to-br ${stat.color} shadow-lg shrink-0 group-hover:scale-110 transition-transform`}>
                    <stat.icon className="w-5 h-5 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        ))}
      </div>

      {/* Navigation Cards - Row 1: News, Tenders, Projects, Companies */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {row1Cards.map((item) => (
          <ImageNavCard key={item.key} item={item} />
        ))}
      </div>

      {/* Navigation Cards - Row 2: Opportunities, Contacts, Reports, My Profile */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {row2Cards.map((item) => (
          <ImageNavCard key={item.key} item={item} />
        ))}
      </div>
    </div>
  );
}
